import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Context as LanguageContext } from '../../../../LanguageProvider';
import { useSelector } from 'react-redux';
import { canAccessStatisticsFeature } from '../../../../../../config/containers/FlowStats/FlowStatsList';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { Box, Button, Checkbox, CircularProgress, Collapse, FormControlLabel, IconButton, MenuItem, Portal, Switch, Tab, Tabs, TextField, Tooltip as MuiTooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import UpdateIcon from '@mui/icons-material/Update';
import AddIcon from '@mui/icons-material/Add';
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Line, Legend, ReferenceArea } from 'recharts';
import FormattedMessage from '../../../../../components/TranslationWrapper';
import globalMessages from '../../../../../../config/globalMessages';
import {
  EXCLUDE_TIMES_CURSOR,
  DIMENSION_SWITCH_COLORS,
  PRODUCTION_STOP_COLOR,
  RESTORE_TIMES_CURSOR,
  OVERVIEW_AVERAGE_KEY,
  OVERVIEW_DATASET,
  OVERVIEW_DATASET_KEY,
  RANGE_PRESET_HOURS,
  SIGNAL_MAIN_FIELD,
  SIGNAL_OVERLAP_DATASET,
  SIGNAL_OVERLAP_DATASET_KEY,
  SIGNAL_OVERLAP_FIELDS,
  STATISTICS_THEME as T
} from './statisticsOverlayConstants';
import { DEFAULT_LIVE_WINDOW_MINUTES, DEFAULT_REFRESH_INTERVAL_MS, RAW_MACHINE_STAT_ENDPOINT } from './machineStatDataConstants';
import {
  addCumulativeOverlayGraphData,
  buildOverviewData,
  buildOverviewEfficiencyDatasets,
  buildOverviewDetails,
  buildOverviewSummary,
  buildSignalOverlapData,
  buildDatasetDoubleAverages,
  buildDatasetEfficiencyData,
  buildDatasetEfficiencySummary,
  buildDatasetErrorTotals,
  buildErrorHighlightRanges,
  buildNoFlowHighlightRanges,
  calculateDatasetErrorDurationMinutes,
  calculateDatasetNoFlowDurationMinutes,
  calculateOverviewErrorDurationMinutes,
  calculateOverviewNoFlowDurationMinutes,
  calculateRangeDurationMinutes,
  formatAxisTick,
  formatDuration,
  formatErrorDuration,
  formatPercentValue,
  formatStatValue,
  formatTooltipDate,
  getProductionStopReason,
  formatTooltipMetricValue,
  getErrorDurationColor,
  getErrorSelectionKey,
  getPercentageSeverityColor,
  getSortedSelectionRange,
  toDisplaySpeed,
  toLocalInput
} from './statisticsOverlayHelpers';
import { getAllStatisticsAreaIdentifiers, getDoubleAreaIdentifiers, getEfficiencyAreaIdentifiers, getErrorAreaIdentifiers } from './statisticsDatasetHelpers';
import { buildEmptyData, buildSummaryByArea, filterDataByRecentMinutes, normalizeGroupedResponse } from './machineStatDataHelpers';
import useStatisticsTimeControls from './useStatisticsTimeControls';
import useStatisticsExport from './useStatisticsExport';
import useAverageLengthStatistics from './useAverageLengthStatistics';
import useProductionEffectiveTime from './useProductionEffectiveTime';
import { getExcludedMinutes, getProductionTimePosition, mergeProductionGaps } from './productionTimeHelpers';
import useResizablePanel from '../../../../LineStatusViewer/hooks/useResizablePanel';
import MetricCard from './MetricCard';
import metricCalculationDescriptions from './metricCalculationDescriptions';
import ErrorSpectrogram from './ErrorSpectrogram';
import StatisticsSpectrogram from './StatisticsSpectrogram';
import ProductionStopLog, { ProductionStopDialog } from './ProductionStopLog';
import useEntryData from './useEntryData';
import EntryDataFilters from './EntryDataFilters';
import useStatisticsCameras from './useStatisticsCameras';
import VideocamIcon from '@mui/icons-material/Videocam';

const STATISTICS_CHART_ID = 'statistics-panel-chart';

const StyledSidebarShell = styled(Box)(() => ({
  backdropFilter: 'blur(2px)',
  boxShadow: 'rgb(245 245 245 / 23%) 0px -1px 1px inset',
  background: 'rgb(255 255 255 / 5%)',
  borderRadius: '5px',
  position: 'fixed',
  right: 10,
  top: 'calc(50vh + 28px)',
  maxHeight: 'calc(100vh - 72px)',
  maxWidth: 'calc(100vw - 20px)',
  pointerEvents: 'auto',
  transition: 'transform 220ms ease'
}));

const StyledSidebarPanel = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  borderRadius: '16px',
  background: 'rgba(235, 237, 239, 0.6)',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  backdropFilter: 'blur(5.5px)',
  WebkitBackdropFilter: 'blur(5.5px)',
  border: '1px solid rgba(235, 237, 239, 0.49)',
  overflow: 'hidden'
}));

const APP_SIDEBAR_WIDTH = 192;
const getDefaultSidebarHeight = () => (typeof window === 'undefined' ? 720 : Math.max(420, Math.min(window.innerHeight - 72, 1200)));
const getMaxSidebarWidth = () => (typeof window === 'undefined' ? 1728 : Math.max(520, window.innerWidth - APP_SIDEBAR_WIDTH - 40));

function StatisticsTooltip({ active, payload, label, dataset, dailyProduction, stops = [], timeZone, entryDataLoading, gapExclusions }) {
  const { locale } = useContext(LanguageContext);
  const point = payload?.[0]?.payload;
  const hasProductionDay = dailyProduction?.coversDate(point?.date);
  const productionCardKeys = (dataset?.productionCards ?? []).map((key) => (key === 'productionTime' ? 'effectiveTime' : key));
  const hoveredProduction = useProductionEffectiveTime({
    dataset: active && point && productionCardKeys.includes('effectiveTime') && !hasProductionDay ? dataset : undefined,
    rangeMode: 'range',
    to: point?.date
  });
  if (!active || !payload?.length) return null;

  const bucketStart = Date.parse(point?.date);
  const bucketEnd = bucketStart + (point?.durationMinutes ?? 1) * 60_000;
  const unassignedStopMinutes = gapExclusions?.unassignedStopGaps.reduce((sum, gap) => (gap.start < bucketEnd && gap.end > bucketStart ? sum + gap.minutes : sum), 0) ?? 0;
  const production = hasProductionDay ? dailyProduction : hoveredProduction;
  const productionRows = {
    runningMetres: { label: 'Linear metres', value: formatStatValue(point?.runningMetres, 2, 'm') },
    effectiveTime: {
      label: 'Effective time',
      value: production.loading ? <CircularProgress size={12} aria-label="Loading effective time" /> : formatErrorDuration(production.getEffectiveMinutesAt(point?.date, point?.durationMinutes ?? 1))
    }
  };

  return (
    <div
      style={{
        background: T.tooltipSurface,
        backdropFilter: 'blur(5px)',
        border: `1px solid ${T.borderStrong}`,
        borderRadius: 8,
        padding: '10px 12px',
        boxShadow: '0 12px 28px rgba(0,0,0,0.12)'
      }}>
      <div style={{ color: T.textMuted, fontSize: 12, marginBottom: 6 }}>{formatTooltipDate(label, timeZone)}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: entry.color, fontSize: 12 }}>
          <span>{entry.name}</span>
          <strong>{formatTooltipMetricValue(entry)}</strong>
        </div>
      ))}
      {stops
        .filter((stop) => stop.start < Date.parse(point?.date) + (point?.durationMinutes ?? 1) * 60_000 && stop.end > Date.parse(point?.date))
        .map((stop) => (
          <div
            key={stop.id}
            style={{ color: PRODUCTION_STOP_COLOR, fontSize: 12, maxWidth: 340, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', borderTop: `1px solid ${T.border}`, marginTop: 6, paddingTop: 6 }}>
            <strong>{getProductionStopReason(stop, locale)}</strong> · {stop.issue}
            <div>
              {stop.location ? `${stop.location} · ` : ''}
              {formatStatValue(stop.duration_minutes, 2, 'min')}
            </div>
            <div>
              {formatTooltipDate(stop.started_at, timeZone)} – {formatTooltipDate(stop.ended_at, timeZone)}
            </div>
          </div>
        ))}
      {unassignedStopMinutes > 0 && (
        <div style={{ color: T.expectedSpeed, fontSize: 12, maxWidth: 340, borderTop: `1px solid ${T.border}`, marginTop: 6, paddingTop: 6 }}>
          <FormattedMessage {...globalMessages.statisticsDefaultLedinekGraph} values={{ minutes: formatStatValue(unassignedStopMinutes, 0) }} />
        </div>
      )}
      {dataset?.entryData &&
        (entryDataLoading ? (
          <CircularProgress size={12} aria-label="Loading production data" />
        ) : !point?.entryData?.length ? (
          <div style={{ color: T.textMuted, fontSize: 12 }}>Production data unavailable</div>
        ) : (
          point.entryData.map((context) => (
            <div key={context.start} style={{ borderTop: `1px solid ${T.border}`, marginTop: 6, paddingTop: 6, maxWidth: 340 }}>
              {point.entryData.length > 1 && <div style={{ color: T.textMuted, fontSize: 11 }}>{formatTooltipDate(Math.max(context.start, Date.parse(point.date)), timeZone)}</div>}
              {dataset.entryData.fields.map((field) => (
                <div key={field.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: T.textPrimary, fontSize: 12 }}>
                  <span>{field.label}</span>
                  <strong style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{context.data[field.key] ?? 'Unavailable'}</strong>
                </div>
              ))}
            </div>
          ))
        ))}
      {dataset?.showXcutMeasurements && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: dataset.color, fontSize: 12 }}>
            <span>
              <FormattedMessage {...(point?.xcutDataCarriedForward ? globalMessages.statisticsLastXcutLength : globalMessages.statisticsXcutLength)} />
            </span>
            <strong>{formatStatValue(point?.averageXcutLength, 0, 'mm')}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: dataset.color, fontSize: 12 }}>
            <span>
              <FormattedMessage {...(point?.xcutDataCarriedForward ? globalMessages.statisticsLastDimensions : globalMessages.statisticsDimensions)} />
            </span>
            <strong>
              {point?.averageXcutWidth != null && point?.averageXcutThickness != null
                ? `${formatStatValue(point.averageXcutWidth, 1)} × ${formatStatValue(point.averageXcutThickness, 1)} mm`
                : 'Unavailable'}
            </strong>
          </div>
        </>
      )}
      {productionCardKeys
        .filter((key) => productionRows[key])
        .map((key) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: dataset.color, fontSize: 12 }}>
            <span>{productionRows[key].label}</span>
            <strong>{productionRows[key].value}</strong>
          </div>
        ))}
    </div>
  );
}

function SignalBooleanChart({
  data,
  fields,
  selectionRange,
  accentColor,
  onSelectionStart,
  onSelectionMove,
  onSelectionEnd,
  onSelectionReset,
  showXAxis = false,
  showLegend = false,
  offsetLines = false,
  title = ''
}) {
  return (
    <Box sx={{ position: 'relative', minHeight: 0 }}>
      {title && <Typography sx={{ position: 'absolute', top: 2, left: 10, zIndex: 1, color: fields[0]?.color ?? T.textMuted, fontSize: 11, fontWeight: 800 }}>{title}</Typography>}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: title ? 18 : 8, right: 8, left: 0, bottom: showXAxis ? 4 : 0 }}
          onMouseDown={onSelectionStart}
          onMouseMove={onSelectionMove}
          onMouseUp={onSelectionEnd}
          onMouseLeave={onSelectionReset}
          syncId="signal-overlap">
          <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatAxisTick}
            tick={showXAxis ? { fill: T.textMuted, fontSize: 10 } : false}
            tickLine={false}
            axisLine={showXAxis ? { stroke: T.axisLine } : false}
            height={showXAxis ? 24 : 0}
            minTickGap={18}
          />
          <YAxis yAxisId="signals" domain={[0, 2]} ticks={[0, 1]} tick={{ fill: `${T.textMuted}cc`, fontSize: 10 }} tickLine={false} axisLine={false} width={42} />
          <Tooltip content={<StatisticsTooltip />} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 11, color: T.textPrimary }} />}
          {selectionRange && <ReferenceArea yAxisId="signals" x1={selectionRange[0]} x2={selectionRange[1]} stroke={accentColor} strokeOpacity={0.35} fill={accentColor} fillOpacity={0.18} isFront />}
          {fields.map((field) => (
            <Line
              key={field.key}
              yAxisId="signals"
              type="stepAfter"
              dataKey={offsetLines ? `${field.key}Offset` : field.key}
              name={field.label}
              stroke={field.color}
              strokeWidth={3}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

function StatisticsTimeControls({ rangeMode, statisticsFrom, statisticsTo, setRangeMode, setStatisticsFrom, setStatisticsTo }) {
  const { draftFrom, draftTo, applyPreset, handleFromChange, handleToChange, handleLive } = useStatisticsTimeControls({
    statisticsFrom,
    statisticsTo,
    setRangeMode,
    setStatisticsFrom,
    setStatisticsTo
  });
  const selectedRangeMinutes = calculateRangeDurationMinutes(rangeMode, statisticsFrom, statisticsTo);

  return (
    <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${T.border}` }}>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant={rangeMode === 'live' ? 'contained' : 'outlined'}
          onClick={handleLive}
          sx={{
            minWidth: 0,
            fontWeight: 700,
            textTransform: 'none',
            ...(rangeMode === 'live'
              ? { backgroundColor: T.controlSurface, color: T.textPrimary, '&:hover': { backgroundColor: T.controlSurface } }
              : { borderColor: T.borderStrong, color: T.textPrimary })
          }}>
          Live
        </Button>
        {RANGE_PRESET_HOURS.map((hours) => {
          const isSelectedPreset = rangeMode === 'range' && selectedRangeMinutes !== null && Math.abs(selectedRangeMinutes - hours * 60) < 1;

          return (
            <Button
              key={hours}
              size="small"
              variant={isSelectedPreset ? 'contained' : 'outlined'}
              onClick={() => applyPreset(hours)}
              sx={{
                minWidth: 0,
                fontWeight: 700,
                textTransform: 'none',
                ...(isSelectedPreset
                  ? { backgroundColor: T.controlSurface, color: T.textPrimary, '&:hover': { backgroundColor: T.controlSurface, color: T.textPrimary } }
                  : { borderColor: T.borderStrong, color: T.textPrimary })
              }}>
              {hours}h
            </Button>
          );
        })}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
        <TextField
          size="small"
          type="datetime-local"
          value={draftFrom}
          onChange={handleFromChange}
          InputLabelProps={{ shrink: true }}
          label="From"
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: T.controlSurface, backdropFilter: 'blur(4px)' } }}
        />
        <TextField
          size="small"
          type="datetime-local"
          value={draftTo}
          onChange={handleToChange}
          InputLabelProps={{ shrink: true }}
          label="To"
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: T.controlSurface, backdropFilter: 'blur(4px)' } }}
        />
      </Box>
    </Box>
  );
}

const getErrorNameMessage = (name) => globalMessages[name] ?? { id: `app.globalMessages.errors.${name}`, defaultMessage: name };

function ErrorGroup({
  group,
  totalMinutes,
  totalNoFlowMinutes,
  collapsedErrorGroups,
  setCollapsedErrorGroups,
  selectedErrorKeys,
  setSelectedErrorKeys,
  excludedErrorKeys,
  setExcludedErrorKeys,
  onErrorSelected
}) {
  const isCollapsed = collapsedErrorGroups.has(group.key);

  const toggleCollapsed = () => {
    setCollapsedErrorGroups((previousGroups) => {
      const nextGroups = new Set(previousGroups);
      if (nextGroups.has(group.key)) {
        nextGroups.delete(group.key);
      } else {
        nextGroups.add(group.key);
      }

      return nextGroups;
    });
  };

  const toggleError = (entry) => {
    const errorKey = getErrorSelectionKey(entry.areaIdentifier, entry.metricName ?? entry.name);
    const isSelected = selectedErrorKeys.has(errorKey);
    const isExcluded = excludedErrorKeys.has(errorKey);
    if (!isSelected && !isExcluded) onErrorSelected();

    setSelectedErrorKeys((previousKeys) => {
      const nextKeys = new Set(previousKeys);
      if (isSelected) {
        nextKeys.delete(errorKey);
      } else if (!isExcluded) {
        nextKeys.add(errorKey);
      }
      return nextKeys;
    });

    setExcludedErrorKeys((previousKeys) => {
      const nextKeys = new Set(previousKeys);
      if (isSelected) {
        nextKeys.add(errorKey);
      } else {
        nextKeys.delete(errorKey);
      }
      return nextKeys;
    });
  };
  const errorKeys = group.entries.map((entry) => getErrorSelectionKey(entry.areaIdentifier, entry.metricName ?? entry.name));
  const allErrorsSelected = errorKeys.length > 0 && errorKeys.every((errorKey) => selectedErrorKeys.has(errorKey));

  const toggleAllErrors = () => {
    if (!allErrorsSelected) onErrorSelected();

    setSelectedErrorKeys((previousKeys) => {
      const nextKeys = new Set(previousKeys);
      errorKeys.forEach((errorKey) => {
        if (allErrorsSelected) {
          nextKeys.delete(errorKey);
        } else {
          nextKeys.add(errorKey);
        }
      });
      return nextKeys;
    });

    setExcludedErrorKeys((previousKeys) => {
      const nextKeys = new Set(previousKeys);
      errorKeys.forEach((errorKey) => nextKeys.delete(errorKey));
      return nextKeys;
    });
  };

  return (
    <Box sx={{ display: 'grid', gap: 0.45 }}>
      <Box
        component="button"
        type="button"
        onClick={toggleCollapsed}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
          alignItems: 'center',
          gap: 1,
          border: 0,
          background: 'transparent',
          p: 0,
          color: T.textPrimary,
          cursor: 'pointer',
          textAlign: 'left'
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
          <ChevronRightIcon sx={{ fontSize: 15, color: T.textMuted, transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 120ms ease' }} />
          <Typography component="span" sx={{ color: T.textPrimary, fontSize: 12, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {group.label}
          </Typography>
        </Box>
        <Typography component="span" sx={{ color: T.textHint, fontSize: 10, fontWeight: 700 }}>
          Sum:
        </Typography>
        <Typography component="span" sx={{ minWidth: 56, color: getErrorDurationColor(group.minutes, totalMinutes), fontSize: 12, fontWeight: 800, textAlign: 'right' }}>
          {formatErrorDuration(group.minutes ?? 0)}
        </Typography>
        <Typography component="span" sx={{ minWidth: 56, color: getErrorDurationColor(group.noFlowMinutes, totalNoFlowMinutes), fontSize: 12, fontWeight: 800, textAlign: 'right' }}>
          {formatErrorDuration(group.noFlowMinutes ?? 0)}
        </Typography>
      </Box>
      <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
        <Box sx={{ display: 'grid', gap: 0.5, pl: 2.3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: 1 }}>
            <Box component="label" sx={{ display: 'flex', alignItems: 'center', minWidth: 0, color: T.textMuted, fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>
              <Checkbox checked={allErrorsSelected} onChange={toggleAllErrors} size="small" sx={{ p: 0.35, mr: 0.25, color: T.textHint, '&.Mui-checked': { color: T.actualSpeed } }} />
              <span>Select all</span>
            </Box>
            <Typography sx={{ minWidth: 56, color: T.textHint, fontSize: 9, fontWeight: 800, textAlign: 'right', textTransform: 'uppercase' }}>Error</Typography>
            <Typography sx={{ minWidth: 56, color: T.textHint, fontSize: 9, fontWeight: 800, textAlign: 'right', textTransform: 'uppercase' }}>No Flow</Typography>
          </Box>
          {group.entries.map((entry) => {
            const errorKey = getErrorSelectionKey(entry.areaIdentifier, entry.metricName ?? entry.name);
            const isSelected = selectedErrorKeys.has(errorKey);
            const isExcluded = excludedErrorKeys.has(errorKey);

            return (
              <Box
                key={errorKey}
                component="label"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  border: 0,
                  borderRadius: 1,
                  backgroundColor: isSelected ? 'rgba(37, 99, 235, 0.1)' : isExcluded ? 'rgba(185, 28, 28, 0.08)' : 'transparent',
                  color: T.textMuted,
                  fontSize: 12,
                  p: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': { backgroundColor: isSelected ? 'rgba(37, 99, 235, 0.14)' : isExcluded ? 'rgba(185, 28, 28, 0.12)' : 'rgba(15, 23, 42, 0.04)' }
                }}>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <Checkbox
                    checked={isSelected}
                    indeterminate={isExcluded}
                    onChange={() => toggleError(entry)}
                    size="small"
                    sx={{ p: 0.35, mr: 0.25, color: T.textHint, '&.Mui-checked': { color: T.actualSpeed }, '&.MuiCheckbox-indeterminate': { color: '#b91c1c' } }}
                  />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <FormattedMessage {...getErrorNameMessage(entry.name)} identifier={entry.name} />
                  </span>
                </Box>
                <strong style={{ minWidth: 56, color: getErrorDurationColor(entry.minutes, totalMinutes), textAlign: 'right' }}>{formatErrorDuration(entry.minutes ?? 0)}</strong>
                <strong style={{ minWidth: 56, color: getErrorDurationColor(entry.noFlowMinutes ?? 0, totalNoFlowMinutes), textAlign: 'right' }}>
                  {formatErrorDuration(entry.noFlowMinutes ?? 0)}
                </strong>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function StatisticsPanel({
  datasets,
  statisticsSections,
  statisticsOverlayGraph,
  statisticsOverlayGraphData,
  showOverview,
  showNoFlowHighlights,
  setShowNoFlowHighlights,
  signalOverlapLast = false,
  showContractEfficiency = true,
  showExpectedContractSpeed = true,
  selectedDatasetKey,
  setSelectedDatasetKey,
  timeframeSelectionEndpoint,
  rangeMode,
  statisticsFrom,
  statisticsTo,
  setStatisticsRangeMode,
  setStatisticsFrom,
  setStatisticsTo,
  dataByArea,
  chartDataByArea,
  summaryByArea,
  efficiencyDataByArea,
  dailyProduction,
  gapExclusions,
  productionStops,
  averageLengths,
  loading,
  error,
  lastUpdated
}) {
  const { locale } = useContext(LanguageContext);
  const [selectedSignalOverlapArea, setSelectedSignalOverlapArea] = useState('');
  const privilege = useSelector((state) => state.global?.loginState?.user?.privilege);
  const [isExclusionModeEnabled, setExcludeTimes] = useState(false);
  const [editingStop, setEditingStop] = useState(undefined);
  const [selectingStopRange, setSelectingStopRange] = useState(false);
  const [snapToEdges, setSnapToEdges] = useState(true);
  const snappingStopRange = selectingStopRange && snapToEdges;
  const stopSelectionGaps = useMemo(
    () => (snappingStopRange && !dailyProduction.loading ? mergeProductionGaps(gapExclusions.gaps) : []),
    [snappingStopRange, dailyProduction.loading, gapExclusions.gaps]
  );
  const [hoveredStopId, setHoveredStopId] = useState(null);
  useEffect(() => {
    setEditingStop(undefined);
    setSelectingStopRange(false);
    setHoveredStopId(null);
  }, [selectedDatasetKey, rangeMode, statisticsFrom, statisticsTo]);
  useEffect(() => setExcludeTimes(false), [selectedDatasetKey, rangeMode, statisticsFrom, statisticsTo]);
  const [selectedErrorKeys, setSelectedErrorKeys] = useState(() => new Set());
  const [excludedErrorKeys, setExcludedErrorKeys] = useState(() => new Set());
  const availableDatasets = useMemo(
    () => [...(showOverview === true ? [OVERVIEW_DATASET] : []), ...(signalOverlapLast ? [...datasets, SIGNAL_OVERLAP_DATASET] : [SIGNAL_OVERLAP_DATASET, ...datasets])],
    [datasets, showOverview, signalOverlapLast]
  );
  const signalOverlapAreaOptions = useMemo(() => getAllStatisticsAreaIdentifiers(datasets).map((areaIdentifier) => ({ value: areaIdentifier, label: areaIdentifier })), [datasets]);
  const activeSignalOverlapArea = signalOverlapAreaOptions.some((option) => option.value === selectedSignalOverlapArea) ? selectedSignalOverlapArea : signalOverlapAreaOptions[0]?.value ?? '';
  const [rawSignalOverlapDataByArea, setRawSignalOverlapDataByArea] = useState(() => buildEmptyData(datasets));
  const [rawSignalOverlapLoading, setRawSignalOverlapLoading] = useState(false);
  const [rawSignalOverlapError, setRawSignalOverlapError] = useState('');
  const overviewEfficiencyDatasets = useMemo(() => buildOverviewEfficiencyDatasets(datasets), [datasets]);
  const overviewData = useMemo(() => buildOverviewData(dataByArea, datasets), [dataByArea, datasets]);
  const signalOverlapData = useMemo(() => buildSignalOverlapData(rawSignalOverlapDataByArea, datasets, activeSignalOverlapArea), [activeSignalOverlapArea, datasets, rawSignalOverlapDataByArea]);
  const metricDataByArea = useMemo(
    () => (rangeMode === 'live' ? filterDataByRecentMinutes(efficiencyDataByArea, DEFAULT_LIVE_WINDOW_MINUTES) : efficiencyDataByArea),
    [efficiencyDataByArea, rangeMode]
  );
  const metricSummaryByArea = useMemo(
    () => (rangeMode === 'live' && efficiencyDataByArea === dataByArea ? buildSummaryByArea(metricDataByArea, datasets) : summaryByArea),
    [dataByArea, datasets, efficiencyDataByArea, metricDataByArea, rangeMode, summaryByArea]
  );
  const adjustedSummaryByArea = useMemo(
    () =>
      Object.fromEntries(
        (datasets ?? []).map((dataset) => [
          dataset.key,
          {
            ...metricSummaryByArea?.[dataset.key],
            ...buildDatasetEfficiencySummary(metricDataByArea, dataset, selectedErrorKeys, excludedErrorKeys)
          }
        ])
      ),
    [datasets, excludedErrorKeys, metricDataByArea, metricSummaryByArea, selectedErrorKeys]
  );
  const metricOverviewSummary = useMemo(
    () => buildOverviewSummary(metricDataByArea, datasets, selectedErrorKeys, excludedErrorKeys),
    [datasets, excludedErrorKeys, metricDataByArea, selectedErrorKeys]
  );
  const overviewDetails = useMemo(() => buildOverviewDetails(dataByArea, datasets), [dataByArea, datasets]);
  const selectedDataset = useMemo(() => availableDatasets.find((dataset) => dataset.key === selectedDatasetKey) ?? availableDatasets[0] ?? null, [availableDatasets, selectedDatasetKey]);
  const canExcludeTimes = canAccessStatisticsFeature(privilege, 'excludeTimes', selectedDataset);
  const canAddStop = canAccessStatisticsFeature(privilege, 'addStop', selectedDataset);
  const canEditStop = canAccessStatisticsFeature(privilege, 'editStop', selectedDataset);
  const canDeleteStop = canAccessStatisticsFeature(privilege, 'deleteStop', selectedDataset);
  const canManageCurrentStop = editingStop?.id ? canEditStop : canAddStop;
  const excludeTimes = canExcludeTimes && isExclusionModeEnabled;
  useEffect(() => {
    if (isExclusionModeEnabled && !canExcludeTimes) {
      setExcludeTimes(false);
      gapExclusions.clearGaps();
    }
  }, [canExcludeTimes, isExclusionModeEnabled]);
  useEffect(() => {
    if (!canManageCurrentStop) {
      setEditingStop(undefined);
      setSelectingStopRange(false);
    }
  }, [canManageCurrentStop]);
  const isOverviewSelected = selectedDataset?.key === OVERVIEW_DATASET_KEY;
  const isSignalOverlapSelected = selectedDataset?.key === SIGNAL_OVERLAP_DATASET_KEY;
  const speedUnit = selectedDataset?.speedUnit;
  const speedUnitSuffix = speedUnit ? ` (${speedUnit})` : '';
  const averageLengthIdentifier = selectedDataset?.averageLengthIdentifier;
  const { dayStart: productionDay, effectiveMinutes } = dailyProduction;
  const productionDays = useMemo(() => dailyProduction.days?.filter((day) => day.productionStart), [dailyProduction.days]);
  const productionExclusions = gapExclusions.exclusionsByArea[selectedDataset?.key] ?? [];
  const productionDate = productionDay ? new Date(productionDay).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const productionFirstDate = rangeMode === 'live' ? productionDate : new Date(statisticsFrom).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const productionPeriod = productionFirstDate === productionDate ? productionDate : `${productionFirstDate} - ${productionDate}`;
  const { averageLength: fetchedAverageLength } = useAverageLengthStatistics({
    dataset: selectedDataset?.calculateExpectedSpeed ? undefined : selectedDataset,
    rangeMode,
    from: statisticsFrom,
    to: statisticsTo
  });
  const averageLength = selectedDataset?.calculateExpectedSpeed ? averageLengths[averageLengthIdentifier] ?? null : fetchedAverageLength;
  useEffect(() => {
    setSelectedErrorKeys(new Set());
    setExcludedErrorKeys(new Set());
  }, [selectedDataset?.key]);
  useEffect(() => {
    if (!isSignalOverlapSelected || !activeSignalOverlapArea || (rangeMode === 'range' && (!statisticsFrom || !statisticsTo))) {
      setRawSignalOverlapLoading(false);
      return undefined;
    }

    let isCancelled = false;

    const fetchRawSignalOverlapData = async () => {
      setRawSignalOverlapLoading(true);
      setRawSignalOverlapError('');

      try {
        const params =
          rangeMode === 'range'
            ? { area_identifier: activeSignalOverlapArea, from: statisticsFrom, to: statisticsTo }
            : { area_identifier: activeSignalOverlapArea, window_minutes: DEFAULT_LIVE_WINDOW_MINUTES };
        const response = await axios.get(RAW_MACHINE_STAT_ENDPOINT, { params });
        const normalizedData = normalizeGroupedResponse(response?.data, datasets);

        if (!isCancelled) {
          setRawSignalOverlapDataByArea(normalizedData);
        }
      } catch (fetchError) {
        if (!isCancelled) {
          setRawSignalOverlapDataByArea(buildEmptyData(datasets));
          setRawSignalOverlapError(fetchError?.message ?? 'Failed to load signal overlap data.');
        }
      } finally {
        if (!isCancelled) {
          setRawSignalOverlapLoading(false);
        }
      }
    };

    fetchRawSignalOverlapData();

    if (rangeMode !== 'live') {
      return () => {
        isCancelled = true;
      };
    }

    const timerId = window.setInterval(fetchRawSignalOverlapData, DEFAULT_REFRESH_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(timerId);
    };
  }, [activeSignalOverlapArea, datasets, isSignalOverlapSelected, rangeMode, statisticsFrom, statisticsTo]);
  const datasetData = useMemo(() => {
    if (!selectedDataset) return [];
    if (isOverviewSelected) return overviewData;
    if (isSignalOverlapSelected) return signalOverlapData;
    return buildDatasetEfficiencyData(chartDataByArea, selectedDataset);
  }, [chartDataByArea, dataByArea, isOverviewSelected, isSignalOverlapSelected, overviewData, selectedDataset, signalOverlapData]);
  const displayDatasetData = useMemo(
    () =>
      speedUnit === 'm/h'
        ? datasetData.map((point) => ({
            ...point,
            actualSpeed: toDisplaySpeed(point.actualSpeed, speedUnit),
            expectedSpeed: toDisplaySpeed(point.expectedSpeed, speedUnit),
            contractSpeed: toDisplaySpeed(point.contractSpeed, speedUnit)
          }))
        : datasetData,
    [datasetData, speedUnit]
  );
  const cameraViews = useStatisticsCameras({ datasetKey: selectedDataset?.key, cameraNames: selectedDataset?.cameraNames, initialTimestamp: datasetData[0]?.date });
  const entryData = useEntryData({ dataset: selectedDataset, points: displayDatasetData, rangeMode, from: statisticsFrom, to: statisticsTo });
  const [entryFilters, setEntryFilters] = useState([]);
  const [entryFilterContainer, setEntryFilterContainer] = useState(null);
  const allowEntryFiltering = selectedDataset?.entryData?.allowFiltering === true;
  useEffect(() => setEntryFilters([]), [selectedDatasetKey, allowEntryFiltering]);
  const entryDatasetData = useMemo(() => {
    const entries = entryData.entries;
    if (!entries.length) return displayDatasetData;
    let index = 0;
    return displayDatasetData.map((point) => {
      const start = Date.parse(point.date);
      const end = start + (point.durationMinutes ?? 1) * 60_000;
      while (index < entries.length && entries[index].end <= start) index += 1;
      const pointEntries = [];
      for (let next = index; next < entries.length && entries[next].start < end; next += 1) pointEntries.push(entries[next]);
      return { ...point, entryData: pointEntries };
    });
  }, [displayDatasetData, entryData.entries]);
  const entryFilterConfiguration = useMemo(
    () => ({ exact: Object.fromEntries((selectedDataset?.entryData?.fields ?? []).map((field) => [field.key, { label: field.label, valType: 'string' }])) }),
    [selectedDataset?.entryData]
  );
  const statisticsOverlayView = isOverviewSelected ? 'overview' : isSignalOverlapSelected ? null : 'dataset';
  const showStatisticsOverlayGraph = statisticsOverlayView && statisticsOverlayGraph?.statisticsOverlay?.views?.includes(statisticsOverlayView);
  const statisticsOverlaySeries = useMemo(
    () =>
      (statisticsOverlayGraph?.statisticsOverlay?.series ?? []).map((series) => ({
        ...series,
        dataKey: `${statisticsOverlayGraph.key}_${series.identifier}Cumulative`
      })),
    [statisticsOverlayGraph]
  );
  const chartData = useMemo(
    () =>
      showStatisticsOverlayGraph
        ? addCumulativeOverlayGraphData({
            chartData: entryDatasetData,
            graphData: statisticsOverlayGraphData,
            graph: statisticsOverlayGraph,
            series: statisticsOverlaySeries
          })
        : entryDatasetData,
    [entryDatasetData, showStatisticsOverlayGraph, statisticsOverlayGraph, statisticsOverlayGraphData, statisticsOverlaySeries]
  );
  const entryPlotRanges = useMemo(() => {
    if (!allowEntryFiltering) return [];
    return entryFilters.flatMap((filter) => {
      const ranges = [];
      entryData.entries.forEach((entry) => {
        if (entry.data[filter.var] == null || String(entry.data[filter.var]) !== String(filter.value1)) return;
        const previous = ranges[ranges.length - 1];
        if (previous?.end === entry.start) previous.end = entry.end;
        else ranges.push({ start: entry.start, end: entry.end });
      });
      return ranges
        .map((range) => ({ ...range, filterId: filter.id, color: filter.color, left: getProductionTimePosition(chartData, range.start), right: getProductionTimePosition(chartData, range.end) }))
        .filter((range) => range.right > range.left);
    });
  }, [chartData, entryData.entries, entryFilters, allowEntryFiltering]);
  const entrySpectrogramField = selectedDataset?.entryData?.fields.find((field) => field.key === selectedDataset.spectrogramField);
  const entrySpectrogramData = useMemo(() => {
    if (!entrySpectrogramField) return [];
    const sections = [];
    entryData.entries.forEach((entry) => {
      const value = entry.data[entrySpectrogramField.key];
      const previous = sections[sections.length - 1];
      if (previous && previous.value === value && previous.end === entry.start) previous.end = entry.end;
      else sections.push({ start: entry.start, end: entry.end, value });
    });
    return sections
      .map((section, index) => ({
        key: section.start,
        left: getProductionTimePosition(chartData, section.start),
        right: getProductionTimePosition(chartData, section.end),
        value: section.value,
        color: section.value != null && section.value !== '' ? DIMENSION_SWITCH_COLORS[index % 2] : 'transparent',
        tooltip: `${entrySpectrogramField.label}: ${section.value ?? 'Unavailable'} · ${formatTooltipDate(Math.max(section.start, Date.parse(chartData[0]?.date)))} – ${formatTooltipDate(
          Math.min(section.end, Date.parse(chartData[chartData.length - 1]?.date))
        )}`
      }))
      .filter((section) => section.right > section.left);
  }, [entryData.entries, entrySpectrogramField, chartData]);
  const dimensionSpectrogramData = useMemo(() => {
    if (!selectedDataset?.showDimensionSpectrogram) return [];
    let dimensions;
    const sections = [];
    chartData.forEach((point, index) => {
      dimensions = point.xcutDimensions ?? dimensions;
      const value = dimensions ? `${formatStatValue(dimensions.width, 1)} × ${formatStatValue(dimensions.thickness, 1)} mm` : undefined;
      const color = dimensions ? DIMENSION_SWITCH_COLORS[dimensions.group % 2] : 'transparent';
      const previous = sections[sections.length - 1];
      if (previous && previous.value === value && previous.color === color) previous.right = (index + 1) / chartData.length;
      else
        sections.push({
          key: point.date,
          left: index / chartData.length,
          right: (index + 1) / chartData.length,
          color,
          value,
          tooltip: `${formatTooltipDate(point.date)}: ${value ? `${value} (W × T)` : 'Unavailable'}`
        });
    });
    return sections;
  }, [chartData, selectedDataset?.showDimensionSpectrogram]);
  const bestHourPlotRanges = useMemo(() => {
    if (!selectedDataset?.highlightBestHour || dailyProduction.loading) return [];
    return (productionDays ?? [])
      .filter((day) => day.bestHour)
      .map((day) => ({
        key: day.dayStart,
        left: getProductionTimePosition(chartData, day.bestHour.start),
        right: getProductionTimePosition(chartData, day.bestHour.end)
      }))
      .filter((range) => range.right > range.left);
  }, [chartData, dailyProduction.loading, productionDays, selectedDataset?.highlightBestHour]);

  const visibleStatisticsOverlaySeries = showStatisticsOverlayGraph
    ? statisticsOverlaySeries.filter((series) => chartData.some((point) => typeof point?.[series.dataKey] === 'number' && Number.isFinite(point[series.dataKey])))
    : [];
  const selectedSummary = useMemo(() => {
    if (!selectedDataset || isSignalOverlapSelected) return null;
    if (isOverviewSelected) return metricOverviewSummary;
    return adjustedSummaryByArea?.[selectedDataset.key] ?? null;
  }, [adjustedSummaryByArea, isOverviewSelected, isSignalOverlapSelected, metricOverviewSummary, selectedDataset]);
  const selectedErrorEntries = useMemo(() => {
    if (isSignalOverlapSelected) return [];
    if (isOverviewSelected) {
      return overviewDetails.errors;
    }

    return buildDatasetErrorTotals(dataByArea, selectedDataset);
  }, [dataByArea, isOverviewSelected, isSignalOverlapSelected, overviewDetails, selectedDataset]);
  const selectedErrorDurationMinutes = useMemo(() => {
    if (isSignalOverlapSelected) return 0;
    if (isOverviewSelected) {
      return calculateOverviewErrorDurationMinutes(dataByArea, datasets);
    }

    return calculateDatasetErrorDurationMinutes(dataByArea, selectedDataset);
  }, [dataByArea, datasets, isOverviewSelected, isSignalOverlapSelected, selectedDataset]);
  const selectedNoFlowDurationMinutes = useMemo(() => {
    if (isOverviewSelected) return calculateOverviewNoFlowDurationMinutes(dataByArea, datasets);
    return calculateDatasetNoFlowDurationMinutes(dataByArea, selectedDataset);
  }, [dataByArea, datasets, isOverviewSelected, selectedDataset]);
  const selectedErrorGroups = useMemo(() => {
    if (isSignalOverlapSelected) return [];
    if (isOverviewSelected) {
      return (datasets ?? [])
        .map((dataset) => ({
          key: `overview:${dataset.key}`,
          label: dataset.label,
          minutes: calculateDatasetErrorDurationMinutes(dataByArea, dataset),
          noFlowMinutes: calculateDatasetNoFlowDurationMinutes(dataByArea, dataset),
          entries: buildDatasetErrorTotals(dataByArea, dataset)
        }))
        .filter((group) => group.entries.length || group.minutes > 0 || group.noFlowMinutes > 0);
    }

    return selectedDataset
      ? [
          {
            key: `area:${selectedDataset.key}`,
            label: selectedDataset.label,
            minutes: selectedErrorDurationMinutes,
            noFlowMinutes: selectedNoFlowDurationMinutes,
            entries: selectedErrorEntries
          }
        ].filter((group) => group.entries.length || group.minutes > 0 || group.noFlowMinutes > 0)
      : [];
  }, [dataByArea, datasets, isOverviewSelected, isSignalOverlapSelected, selectedDataset, selectedErrorDurationMinutes, selectedNoFlowDurationMinutes, selectedErrorEntries]);
  const errorHighlightRanges = useMemo(() => {
    if (isSignalOverlapSelected) return [];
    const areaIdentifiers = isOverviewSelected ? [...new Set((datasets ?? []).flatMap((dataset) => getErrorAreaIdentifiers(dataset)))] : getErrorAreaIdentifiers(selectedDataset);
    return buildErrorHighlightRanges(dataByArea, areaIdentifiers, datasetData, selectedErrorKeys, excludedErrorKeys);
  }, [dataByArea, datasetData, datasets, excludedErrorKeys, isOverviewSelected, isSignalOverlapSelected, selectedDataset, selectedErrorKeys]);
  const noFlowHighlightRanges = useMemo(() => {
    if (isSignalOverlapSelected) return [];
    const areaIdentifiers = isOverviewSelected ? [...new Set((datasets ?? []).flatMap((dataset) => getErrorAreaIdentifiers(dataset)))] : getErrorAreaIdentifiers(selectedDataset);
    return buildNoFlowHighlightRanges(dataByArea, areaIdentifiers, datasetData, selectedErrorKeys, excludedErrorKeys);
  }, [dataByArea, datasetData, datasets, excludedErrorKeys, isOverviewSelected, isSignalOverlapSelected, selectedDataset, selectedErrorKeys]);
  const errorAreaIdentifiers = useMemo(
    () => (isOverviewSelected ? [...new Set((datasets ?? []).flatMap((d) => getErrorAreaIdentifiers(d)))] : getErrorAreaIdentifiers(selectedDataset)),
    [datasets, isOverviewSelected, selectedDataset]
  );
  const selectedDoubleEntries = useMemo(() => {
    if (isSignalOverlapSelected) return [];
    if (isOverviewSelected) {
      return overviewDetails.doubles;
    }

    return buildDatasetDoubleAverages(dataByArea, selectedDataset);
  }, [dataByArea, isOverviewSelected, isSignalOverlapSelected, overviewDetails, selectedDataset]);
  const [selectionStart, setSelectionStart] = useState('');
  const [selectionEnd, setSelectionEnd] = useState('');
  const [timeframeStatus, setTimeframeStatus] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isChartFullscreen, setIsChartFullscreen] = useState(false);
  const chartFullscreen = isChartFullscreen || selectingStopRange;
  const [collapsedErrorGroups, setCollapsedErrorGroups] = useState(() => new Set());
  const [showErrorHighlights, setShowErrorHighlights] = useState(false);
  const { panelSize, setPanelSize, startResizing } = useResizablePanel({
    initialWidth: 1200,
    initialHeight: getDefaultSidebarHeight(),
    minWidth: 520,
    maxWidth: getMaxSidebarWidth(),
    minHeight: 420,
    maxHeight: 1200
  });
  const panelWidthBeforeExpand = useRef(panelSize.width);
  const isPanelExpanded = panelSize.width >= getMaxSidebarWidth();
  const togglePanelWidth = () => {
    const width = isPanelExpanded ? Math.min(panelWidthBeforeExpand.current, getMaxSidebarWidth()) : getMaxSidebarWidth();
    if (!isPanelExpanded) panelWidthBeforeExpand.current = panelSize.width;
    setPanelSize((current) => ({ ...current, width }));
  };
  const accentColor = selectedDataset?.color ?? '#10b981';
  const bestHourValue = (hour) =>
    hour ? (
      <>
        {formatAxisTick(hour.start)}–{formatAxisTick(hour.end)}
        <Box component="span" sx={{ display: 'block', fontSize: 14 }}>
          {formatStatValue(hour.metres, 2, 'm')}
        </Box>
      </>
    ) : (
      'Unavailable'
    );
  const productionTimes = (field) =>
    productionDays
      ?.map((entry) => {
        const time = entry[field] ? new Date(entry[field]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : 'Not started';
        const date = new Date(entry.dayStart).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return `${time} (${date})`;
      })
      .join(', ') ?? 'Unavailable';
  const dailyValues = (renderValue) =>
    productionDays?.length ? (
      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {productionDays.map((day) => (
          <Box component="span" key={day.dayStart}>
            {dailyProduction.days.length > 1 && (
              <Box component="span" sx={{ display: 'block', fontSize: 12 }}>
                {new Date(day.dayStart).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </Box>
            )}
            {renderValue(day)}
          </Box>
        ))}
      </Box>
    ) : (
      'Unavailable'
    );
  const productionCards = {
    runningMetres: {
      label: 'Linear meters',
      value: dailyValues((day) => formatStatValue(day.runningMetres, 2, 'm'))
    },
    productionTime: {
      label: 'Production time',
      value: (
        <>
          {dailyValues((day) => {
            const excludedMinutes = day.productionStart
              ? getExcludedMinutes(Date.parse(day.productionStart), Date.parse(day.productionEnd) + 60_000, productionExclusions, gapExclusions.noFlowEntries)
              : 0;
            return (
              <>
                {day.productionStart ? formatAxisTick(day.productionStart) : 'Not started'} – {day.productionEnd ? formatAxisTick(day.productionEnd) : 'Not started'}
                <Box component="span" sx={{ display: 'block', fontSize: 14 }}>
                  <FormattedMessage
                    {...globalMessages.statisticsProductionTimeDurations}
                    values={{
                      total: formatErrorDuration(day.effectiveMinutes),
                      net: formatErrorDuration(day.effectiveMinutes - excludedMinutes)
                    }}
                  />
                </Box>
              </>
            );
          })}
          {productionDays?.length > 1 && (
            <Box component="span" sx={{ display: 'block', fontSize: 14, borderTop: `1px solid ${T.border}`, mt: 1, pt: 0.75 }}>
              <FormattedMessage
                {...globalMessages.statisticsProductionTimeSum}
                values={{
                  total: formatErrorDuration(effectiveMinutes),
                  net: formatErrorDuration(gapExclusions.netRunningMinutes)
                }}
              />
            </Box>
          )}
        </>
      )
    },
    productionStart: {
      label: productionPeriod ? `Start of production (${productionPeriod})` : 'Start of production',
      value: productionTimes('productionStart')
    },
    productionEnd: {
      label: productionPeriod ? `End of production (${productionPeriod})` : 'End of production',
      value: productionTimes('productionEnd')
    },
    effectiveTime: { label: productionPeriod ? `Effective time for ${productionPeriod}` : 'Effective time', value: formatErrorDuration(effectiveMinutes) },
    netRunningTime: { label: productionPeriod ? `Net running time for ${productionPeriod}` : 'Net running time', value: formatErrorDuration(gapExclusions.netRunningMinutes) },
    bestHour: {
      label: productionPeriod ? `Best hour (${productionPeriod})` : 'Best hour',
      value: dailyValues((day) => bestHourValue(day.bestHour))
    }
  };
  const efficiencyMetricColor = getPercentageSeverityColor(selectedSummary?.fullRangeEfficiency);
  const contractEfficiencyMetricColor = getPercentageSeverityColor(selectedSummary?.fullRangeContractEfficiency);
  const isErrorHighlightActive = showErrorHighlights && errorHighlightRanges.length > 0;
  const isNoFlowHighlightActive = showNoFlowHighlights && noFlowHighlightRanges.length > 0;
  const chartLoading = isSignalOverlapSelected ? rawSignalOverlapLoading : loading;
  const chartError = isSignalOverlapSelected ? rawSignalOverlapError : error;
  const stopClassificationLoading = chartLoading || dailyProduction.loading || productionStops.loading;
  const unassignedStopMinutes = stopClassificationLoading ? undefined : productionStops.error || chartError ? null : gapExclusions.unassignedStopMinutes;

  const rangeLabel = rangeMode === 'live' ? `Live · Last ${DEFAULT_LIVE_WINDOW_MINUTES} minutes` : `${statisticsFrom || 'N/A'} → ${statisticsTo || 'N/A'}`;
  const liveMetricLabelSuffix = rangeMode === 'live' ? ` (last ${DEFAULT_LIVE_WINDOW_MINUTES} minutes)` : '';
  const selectedRangeDuration = formatDuration(calculateRangeDurationMinutes(rangeMode, statisticsFrom, statisticsTo));

  const metricCards = {
    efficiency: { label: `Efficiency${liveMetricLabelSuffix}`, value: formatPercentValue(selectedSummary?.fullRangeEfficiency), accent: efficiencyMetricColor },
    contractEfficiency: { label: `Contract Efficiency${liveMetricLabelSuffix}`, value: formatPercentValue(selectedSummary?.fullRangeContractEfficiency), accent: contractEfficiencyMetricColor },
    timeframe: { label: 'Timeframe', value: selectedRangeDuration, accent: T.textPrimary },
    actualSpeedAverage: {
      label: `${selectedDataset?.actualSpeedLabel ?? 'Actual Speed Avg'}${liveMetricLabelSuffix}`,
      value: formatStatValue(toDisplaySpeed(selectedSummary?.actualSpeedAverage, speedUnit), 2, speedUnit),
      accent: T.actualSpeed
    },
    expectedSpeedAverage: {
      label: `${selectedDataset?.expectedSpeedLabel ?? 'Expected Speed Avg'}${liveMetricLabelSuffix}`,
      value: formatStatValue(toDisplaySpeed(selectedSummary?.expectedSpeedAverage, speedUnit), 2, speedUnit),
      accent: T.expectedSpeed
    },
    contractSpeedAverage: {
      label: `Expected Contract Speed${liveMetricLabelSuffix}`,
      value: formatStatValue(toDisplaySpeed(selectedSummary?.contractSpeedAverage, speedUnit), 2, speedUnit),
      accent: T.contractSpeed
    },
    averageLength: averageLengthIdentifier
      ? {
          label: `${averageLengthIdentifier === 'XCUT_BOARD_LENGTH' ? 'Xcut data' : 'Average Length'}${liveMetricLabelSuffix}`,
          value: averageLength === null ? 'Unavailable' : `${formatStatValue(averageLength, 0)} mm`,
          accent: accentColor
        }
      : null
  };
  const metricCardOrder = selectedDataset?.summaryCards ?? [
    'efficiency',
    ...(showContractEfficiency ? ['contractEfficiency'] : []),
    'timeframe',
    'actualSpeedAverage',
    'expectedSpeedAverage',
    ...(showExpectedContractSpeed ? ['contractSpeedAverage'] : []),
    'averageLength'
  ];

  const cardCalculations = metricCalculationDescriptions({
    dataset: selectedDataset,
    summary: selectedSummary,
    days: productionDays,
    effectiveMinutes,
    netMinutes: gapExclusions.netRunningMinutes,
    rangeMinutes: calculateRangeDurationMinutes(rangeMode, statisticsFrom, statisticsTo),
    rangeLabel,
    unassignedStopMinutes
  });
  const overviewCalculation = (field) => {
    const values = selectedSummary?.efficiencyCalculations?.filter((item) => item[field] != null) ?? [];
    return (
      <FormattedMessage
        {...globalMessages.statisticsOverviewEfficiencyCalculation}
        values={{
          values: values.map((item) => formatStatValue(item[field], 2)).join(' + '),
          count: values.length,
          efficiency: formatStatValue(selectedSummary?.[field === 'efficiency' ? 'fullRangeEfficiency' : 'fullRangeContractEfficiency'], 2)
        }}
      />
    );
  };

  const [exporting, setExporting] = useState(false);

  const exportFilename = `statistics_${(selectedDataset?.label ?? 'export').replace(/\s+/g, '_')}`;
  const exportTitle = isSignalOverlapSelected ? `Signal Overlap · ${activeSignalOverlapArea} · ${rangeLabel}` : `${selectedDataset?.label ?? 'Statistics'} · ${rangeLabel}`;

  const { handleExportJSON, handleExportCSV, handleExportPDF } = useStatisticsExport({
    isSignalOverlapSelected,
    isOverviewSelected,
    activeSignalOverlapArea,
    rawSignalOverlapDataByArea,
    selectedSummary,
    datasets,
    dataByArea,
    selectedDataset,
    datasetData,
    selectedErrorEntries,
    selectedDoubleEntries,
    overviewData,
    overviewEfficiencyDatasets,
    signalOverlapData,
    adjustedSummaryByArea,
    exportFilename,
    exportTitle,
    setExporting
  });

  const resetSelection = () => {
    cameraViews.cancelPreview();
    setSelectionStart('');
    setSelectionEnd('');
  };

  const leaveExclusions = () => {
    setExcludeTimes(false);
    gapExclusions.clearGaps();
    resetSelection();
  };
  const openStop = (stop = null) => {
    if (stop?.id ? !canEditStop : !canAddStop) return;
    leaveExclusions();
    setSnapToEdges(true);
    setEditingStop(stop ?? { started_at: new Date().toISOString() });
  };

  const beginStopSelection = (form) => {
    setEditingStop((current) => ({ ...current, draft: form }));
    resetSelection();
    setSelectingStopRange(true);
  };
  const cancelStopSelection = () => {
    resetSelection();
    setSelectingStopRange(false);
  };

  const selectStopRange = (start, end) => {
    setEditingStop((current) => ({
      ...current,
      draft: { ...current.draft, start: toLocalInput(start), duration: (end - start) / 60_000 }
    }));
    cancelStopSelection();
  };
  const handleStopGapClick = (chartState) => {
    const time = Date.parse(chartState?.activeLabel);
    const gap = stopSelectionGaps.find((gap) => time >= gap.start && time < gap.end);
    if (gap) selectStopRange(gap.start, gap.end);
  };

  const handleChartSelectionStart = (chartState) => {
    cameraViews.cancelPreview();
    chartState?.sourceEvent?.preventDefault?.();
    const activeLabel = chartState?.activeLabel;
    if (!activeLabel || datasetData.length === 0) return;

    setTimeframeStatus('');
    setSelectionStart(activeLabel);
    setSelectionEnd(activeLabel);
  };

  const handleChartSelectionMove = (chartState) => {
    if (!selectionStart && !selectingStopRange) cameraViews.seek(chartState?.activeLabel);
    if (!selectionStart || !chartState?.activeLabel) return;

    setSelectionEnd(chartState.activeLabel);
  };

  const handleGapClick = (chartState) => {
    const time = Date.parse(chartState?.activeLabel);
    const gap = gapExclusions.gaps.find((gap) => time >= gap.start && time < gap.end);
    const stop = productionStops.stops.find((entry) => time >= entry.start && time < entry.end);
    if (stop) gapExclusions.toggleStop(stop);
    else if (gap) gapExclusions.toggleGap(gap.key);
  };

  const extendTimeframe = (direction) => {
    const to = rangeMode === 'live' ? new Date() : new Date(statisticsTo);
    const from = rangeMode === 'live' ? new Date(to.getTime() - DEFAULT_LIVE_WINDOW_MINUTES * 60_000) : new Date(statisticsFrom);
    const boundary = direction < 0 ? from : to;
    boundary.setTime(boundary.getTime() + direction * 60 * 60_000);
    setStatisticsFrom(toLocalInput(from));
    setStatisticsTo(toLocalInput(to));
    setStatisticsRangeMode('range');
  };

  const handleChartSelectionEnd = async () => {
    if (!selectionStart || !selectionEnd || selectionStart === selectionEnd) {
      if (selectionStart && !selectingStopRange) cameraViews.seek(selectionStart, true);
      resetSelection();
      return;
    }

    const [fromDate, toDate] = [selectionStart, selectionEnd].sort((left, right) => Date.parse(left) - Date.parse(right));
    const fromValue = toLocalInput(fromDate);
    const toValue = toLocalInput(toDate);

    if (selectingStopRange) {
      selectStopRange(Date.parse(fromDate), Date.parse(toDate));
      return;
    }

    setStatisticsFrom(fromValue);
    setStatisticsTo(toValue);
    setStatisticsRangeMode('range');
    resetSelection();

    if (!timeframeSelectionEndpoint) return;

    try {
      await axios.post(timeframeSelectionEndpoint, {
        datasetKey: isOverviewSelected || isSignalOverlapSelected ? null : selectedDataset?.key,
        areaIdentifier: isOverviewSelected || isSignalOverlapSelected ? null : selectedDataset?.key,
        efficiencyAreaIdentifiers: isOverviewSelected || isSignalOverlapSelected ? [] : getEfficiencyAreaIdentifiers(selectedDataset),
        errorsAreaIdentifiers: isOverviewSelected || isSignalOverlapSelected ? [] : getErrorAreaIdentifiers(selectedDataset),
        doublesAreaIdentifiers: isOverviewSelected || isSignalOverlapSelected ? [] : getDoubleAreaIdentifiers(selectedDataset),
        from: fromValue,
        to: toValue
      });
      setTimeframeStatus('Timeframe sent');
    } catch {
      setTimeframeStatus('Failed to send timeframe');
    }
  };

  const selectionRange = getSortedSelectionRange(selectionStart, selectionEnd);
  const chartSelectionYAxisId = isSignalOverlapSelected ? 'signals' : isOverviewSelected ? 'efficiency' : 'speed';

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-index-overlay)',
        pointerEvents: 'none'
      }}>
      <StyledSidebarShell
        sx={{
          width: panelSize.width,
          height: panelSize.height,
          transform: isCollapsed ? 'translateY(-50%) translateX(100%)' : 'translateY(-50%) translateX(0)'
        }}>
        <IconButton
          size="small"
          onClick={() => setIsCollapsed((collapsed) => !collapsed)}
          aria-label={isCollapsed ? 'Expand statistics sidebar' : 'Collapse statistics sidebar'}
          sx={{
            position: 'absolute',
            left: -26,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 26,
            height: 42,
            borderRadius: '12px 0 0 12px',
            color: T.textPrimary,
            background: 'rgba(235, 237, 239, 0.85)',
            backdropFilter: 'blur(5.5px)',
            WebkitBackdropFilter: 'blur(5.5px)',
            border: '1px solid rgba(235, 237, 239, 0.49)',
            borderRight: 0,
            boxShadow: '-4px 4px 20px rgba(15, 23, 42, 0.12)',
            zIndex: 30,
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.92)'
            }
          }}>
          {isCollapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Box
          onPointerDown={(event) => startResizing(event, 'left')}
          sx={{
            position: 'absolute',
            top: 0,
            left: -6,
            width: 12,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 25,
            touchAction: 'none'
          }}
        />
        <Box
          onPointerDown={(event) => startResizing(event, 'top')}
          sx={{
            position: 'absolute',
            top: -6,
            left: 0,
            width: '100%',
            height: 12,
            cursor: 'row-resize',
            zIndex: 25,
            touchAction: 'none'
          }}
        />
        <Box
          onPointerDown={(event) => startResizing(event, 'bottom')}
          sx={{
            position: 'absolute',
            bottom: -6,
            left: 0,
            width: '100%',
            height: 12,
            cursor: 'row-resize',
            zIndex: 25,
            touchAction: 'none'
          }}
        />
        <Box
          onPointerDown={(event) => startResizing(event, 'left-top')}
          sx={{
            position: 'absolute',
            top: -8,
            left: -8,
            width: 18,
            height: 18,
            cursor: 'nesw-resize',
            zIndex: 26,
            touchAction: 'none'
          }}
        />
        <Box
          onPointerDown={(event) => startResizing(event, 'left-bottom')}
          sx={{
            position: 'absolute',
            bottom: -8,
            left: -8,
            width: 18,
            height: 18,
            cursor: 'nwse-resize',
            zIndex: 26,
            touchAction: 'none'
          }}
        />
        <StyledSidebarPanel sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.75, py: 1.5, borderBottom: `1px solid ${T.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: T.textPrimary, fontWeight: 800, letterSpacing: 0.2 }}>Production Flow Statistics</Typography>
                <Typography sx={{ color: T.textMuted, fontSize: 12 }}>
                  {selectedDataset?.label ?? 'Dataset'} · {rangeMode === 'live' ? 'Live' : 'Range'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {(loading || rawSignalOverlapLoading) && <UpdateIcon sx={{ color: T.textMuted, animation: 'statisticsSpin 1.1s linear infinite' }} />}
                <MuiTooltip title={<FormattedMessage {...(isPanelExpanded ? globalMessages.collapsePanel : globalMessages.expandPanel)} />}>
                  <IconButton
                    size="small"
                    onClick={togglePanelWidth}
                    aria-label={isPanelExpanded ? 'Collapse panel' : 'Expand panel'}
                    aria-pressed={isPanelExpanded}
                    sx={{ color: T.textPrimary, border: `1px solid ${T.borderStrong}`, borderRadius: 1, '&:hover': { backgroundColor: T.controlSurface } }}>
                    {isPanelExpanded ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                  </IconButton>
                </MuiTooltip>
              </Box>
            </Box>
            <Typography sx={{ color: T.textMuted, fontSize: 11, mt: 0.75 }}>{lastUpdated ? `Updated ${formatTooltipDate(lastUpdated)}` : 'Waiting for data'}</Typography>
          </Box>

          <Tabs
            value={selectedDataset?.key ?? false}
            onChange={(_, nextValue) => setSelectedDatasetKey(nextValue)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 48,
              borderBottom: `1px solid ${T.border}`,
              '& .MuiTabs-indicator': {
                backgroundColor: accentColor
              },
              '& .MuiTab-root': {
                minHeight: 48,
                minWidth: 64,
                textTransform: 'none',
                fontWeight: 700,
                color: T.textMuted
              },
              '& .Mui-selected': {
                color: accentColor
              }
            }}>
            {availableDatasets.map((dataset) => (
              <Tab key={dataset.key} value={dataset.key} label={dataset.label} />
            ))}
          </Tabs>

          <StatisticsTimeControls
            rangeMode={rangeMode}
            statisticsFrom={statisticsFrom}
            statisticsTo={statisticsTo}
            setRangeMode={setStatisticsRangeMode}
            setStatisticsFrom={setStatisticsFrom}
            setStatisticsTo={setStatisticsTo}
          />

          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, flex: 1, overflowY: 'auto' }}>
            {allowEntryFiltering && statisticsSections?.speedChart !== false && (
              <EntryDataFilters
                key={selectedDataset.key}
                configuration={entryFilterConfiguration}
                container={chartFullscreen ? entryFilterContainer : null}
                onChange={setEntryFilters}
                loading={entryData.loading}
                error={entryData.error}
                hasMatches={entryPlotRanges.length > 0}
              />
            )}
            <Portal disablePortal={!chartFullscreen}>
              <Box
                sx={{
                  pointerEvents: 'auto',
                  backgroundColor: T.surface,
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  border: `1px solid ${T.border}`,
                  borderRadius: 1.5,
                  p: chartFullscreen ? 2 : 1.25,
                  flexShrink: 0,
                  ...(chartFullscreen
                    ? {
                        position: 'fixed',
                        inset: 16,
                        zIndex: 'var(--z-index-modal-content)',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)'
                      }
                    : {})
                }}>
                {chartFullscreen && !selectingStopRange && (
                  <Box sx={{ mx: -2, mt: -2, mb: 1 }}>
                    <StatisticsTimeControls
                      rangeMode={rangeMode}
                      statisticsFrom={statisticsFrom}
                      statisticsTo={statisticsTo}
                      setRangeMode={setStatisticsRangeMode}
                      setStatisticsFrom={setStatisticsFrom}
                      setStatisticsTo={setStatisticsTo}
                    />
                  </Box>
                )}
                {chartFullscreen && allowEntryFiltering && <Box ref={setEntryFilterContainer} sx={{ flexShrink: 0 }} />}
                {selectingStopRange && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                    <Typography sx={{ color: PRODUCTION_STOP_COLOR, fontWeight: 700 }}>
                      <FormattedMessage {...(snapToEdges ? globalMessages.stopSnapSelectionHint : globalMessages.stopDragSelectionHint)} />
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={<Switch size="small" checked={snapToEdges} onChange={(event) => setSnapToEdges(event.target.checked)} disabled={dailyProduction.loading} />}
                        label={<FormattedMessage {...globalMessages.snapToEdges} />}
                        sx={{ whiteSpace: 'nowrap' }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={cancelStopSelection}
                        sx={{ border: '1px solid #dc2626', color: '#dc2626', textTransform: 'none', '&:hover': { borderColor: '#dc2626', backgroundColor: '#fee2e2', color: '#dc2626' } }}>
                        Cancel selection
                      </Button>
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Typography sx={{ color: T.textMuted, fontSize: 12 }}>
                    {rangeLabel}
                    {timeframeStatus ? ` · ${timeframeStatus}` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {isSignalOverlapSelected ? (
                      <TextField
                        select
                        size="small"
                        label="Area"
                        value={activeSignalOverlapArea}
                        disabled={!signalOverlapAreaOptions.length}
                        onChange={(event) => setSelectedSignalOverlapArea(event.target.value)}
                        SelectProps={{ MenuProps: { sx: { zIndex: 'calc(var(--z-index-modal-content) + 1)' } } }}
                        sx={{
                          minWidth: 130,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: T.controlSurface,
                            backdropFilter: 'blur(4px)',
                            fontSize: 12,
                            fontWeight: 700
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: 11
                          }
                        }}>
                        {signalOverlapAreaOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowErrorHighlights((isVisible) => !isVisible)}
                          disabled={!errorHighlightRanges.length}
                          sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.25,
                            fontSize: 11,
                            fontWeight: 800,
                            textTransform: 'none',
                            borderColor: '#ed4242',
                            color: '#ed4242',
                            '&:hover': { borderColor: '#ed4242', backgroundColor: '#ed424218', color: '#ed4242' },
                            '&.Mui-disabled': { opacity: 0.4 },
                            ...(isErrorHighlightActive
                              ? { backgroundColor: 'rgba(237, 66, 66, 0.9)', color: '#ffffff', '&:hover': { backgroundColor: 'rgba(237, 66, 66, 0.9)', borderColor: '#ed4242', color: '#ffffff' } }
                              : {})
                          }}>
                          Show Errors
                        </Button>
                        {selectedDataset?.cameraNames?.length > 0 && (
                          <>
                            <Button
                              size="small"
                              variant={cameraViews.enabled ? 'contained' : 'outlined'}
                              startIcon={cameraViews.loading && cameraViews.enabled ? <CircularProgress size={14} color="inherit" /> : <VideocamIcon fontSize="small" />}
                              aria-pressed={cameraViews.enabled}
                              disabled={!cameraViews.enabled && (chartLoading || !datasetData.length || selectingStopRange || excludeTimes)}
                              onClick={cameraViews.toggle}
                              sx={{
                                minWidth: 0,
                                px: 1,
                                py: 0.25,
                                fontSize: 11,
                                fontWeight: 800,
                                textTransform: 'none',
                                color: cameraViews.enabled ? '#fff' : T.actualSpeed,
                                borderColor: T.actualSpeed,
                                backgroundColor: cameraViews.enabled ? T.actualSpeed : 'transparent',
                                '&:hover': { backgroundColor: cameraViews.enabled ? T.actualSpeed : `${T.actualSpeed}18`, borderColor: T.actualSpeed }
                              }}>
                              <FormattedMessage {...(cameraViews.enabled ? globalMessages.disableStatisticsCameras : globalMessages.enableStatisticsCameras)} />
                            </Button>
                            {cameraViews.enabled && cameraViews.error && (
                              <Typography role="alert" sx={{ fontSize: 11, color: 'error.main' }}>
                                <FormattedMessage {...globalMessages[cameraViews.error.message]} values={{ name: cameraViews.error.name }} />
                              </Typography>
                            )}
                          </>
                        )}
                        {selectedDataset?.allowStopAnnotations && canAddStop && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openStop()}
                            disabled={selectingStopRange || productionStops.loading || !productionStops.timeZone}
                            sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11, fontWeight: 800, textTransform: 'none', color: PRODUCTION_STOP_COLOR, borderColor: PRODUCTION_STOP_COLOR }}>
                            Add stop
                          </Button>
                        )}
                        {canExcludeTimes && (selectedDataset?.allowGapExclusions || selectedDataset?.allowStopAnnotations) && (
                          <Button
                            size="small"
                            variant={excludeTimes ? 'contained' : 'outlined'}
                            onClick={() => {
                              resetSelection();
                              if (excludeTimes) gapExclusions.clearGaps();
                              setExcludeTimes((active) => !active);
                            }}
                            disabled={selectingStopRange || dailyProduction.loading || (selectedDataset?.allowStopAnnotations && productionStops.loading)}
                            aria-pressed={excludeTimes}
                            sx={{
                              minWidth: 0,
                              px: 1,
                              py: 0.25,
                              fontSize: 11,
                              fontWeight: 800,
                              textTransform: 'none',
                              color: excludeTimes ? '#ffffff' : '#dc2626',
                              borderColor: '#dc2626',
                              backgroundColor: excludeTimes ? '#dc2626' : 'transparent',
                              '&:hover': { backgroundColor: excludeTimes ? '#b91c1c' : '#dc262618', borderColor: '#dc2626' }
                            }}>
                            Exclude times
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowNoFlowHighlights((isVisible) => !isVisible)}
                          aria-pressed={showNoFlowHighlights}
                          disabled={!showNoFlowHighlights && !noFlowHighlightRanges.length && !gapExclusions.noFlowEntries.some((entry) => entry.aggregateSpeed === 0 && entry.noFlowMinutes > 0)}
                          sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.25,
                            fontSize: 11,
                            fontWeight: 800,
                            textTransform: 'none',
                            borderColor: T.actualSpeed,
                            color: T.actualSpeed,
                            '&:hover': { borderColor: T.actualSpeed, backgroundColor: `${T.actualSpeed}18`, color: T.actualSpeed },
                            '&.Mui-disabled': { opacity: 0.4 },
                            ...(showNoFlowHighlights
                              ? { backgroundColor: 'rgba(37, 99, 235, 0.9)', color: '#ffffff', '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.9)', borderColor: T.actualSpeed, color: '#ffffff' } }
                              : {})
                          }}>
                          Show No Flow
                        </Button>
                      </>
                    )}
                    {[
                      { label: '↓ JSON', handler: handleExportJSON, color: '#6366f1', disabled: !datasetData.length || exporting },
                      { label: '↓ Excel', handler: handleExportCSV, color: '#16a34a', disabled: !datasetData.length || exporting },
                      { label: '↓ PDF', handler: handleExportPDF, color: '#dc2626', disabled: !datasetData.length || exporting }
                    ].map(({ label, handler, color, disabled }) => (
                      <Button
                        key={label}
                        size="small"
                        variant="outlined"
                        onClick={handler}
                        disabled={disabled}
                        sx={{
                          minWidth: 0,
                          px: 1,
                          py: 0.25,
                          fontSize: 11,
                          fontWeight: 800,
                          textTransform: 'none',
                          borderColor: color,
                          color,
                          '&:hover': { borderColor: color, backgroundColor: `${color}18`, color },
                          '&.Mui-disabled': { opacity: 0.4 }
                        }}>
                        {exporting ? '…' : label}
                      </Button>
                    ))}
                    <IconButton
                      size="small"
                      onClick={selectingStopRange ? cancelStopSelection : () => setIsChartFullscreen((isFullscreen) => !isFullscreen)}
                      aria-label={selectingStopRange ? 'Cancel stop selection' : isChartFullscreen ? 'Close fullscreen chart' : 'Open fullscreen chart'}
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1,
                        color: T.textPrimary,
                        border: `1px solid ${T.borderStrong}`,
                        backgroundColor: T.controlSurface,
                        backdropFilter: 'blur(4px)',
                        '&:hover': { backgroundColor: T.tooltipSurface }
                      }}>
                      {chartFullscreen ? <CloseFullscreenIcon sx={{ fontSize: 17 }} /> : <OpenInFullIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                  </Box>
                </Box>

                {(isSignalOverlapSelected || statisticsSections?.speedChart !== false) && (
                  <Box
                    id={STATISTICS_CHART_ID}
                    sx={{
                      position: 'relative',
                      px: 3,
                      height: chartFullscreen ? 'auto' : isSignalOverlapSelected ? 620 : 240,
                      flex: chartFullscreen ? 1 : 'initial',
                      minHeight: 0,
                      cursor: excludeTimes ? EXCLUDE_TIMES_CURSOR : snappingStopRange ? 'default' : datasetData.length ? 'crosshair' : 'default',
                      userSelect: 'none',
                      '& .recharts-line, & .recharts-active-dot': { pointerEvents: excludeTimes || snappingStopRange ? 'none' : undefined },
                      '& .statistics-stop-selection-gap:hover, & .statistics-stop-selection-gap:focus-visible': { animation: 'statisticsStopPulse 1.2s ease-out infinite' }
                    }}>
                    {[-1, 1].map((direction) => (
                      <IconButton
                        key={direction}
                        size="small"
                        aria-label={direction < 0 ? 'Load previous hour' : 'Load next hour'}
                        title={direction < 0 ? 'Load previous hour' : 'Load next hour'}
                        onClick={() => extendTimeframe(direction)}
                        disabled={selectingStopRange || chartLoading || (rangeMode === 'range' && (!statisticsFrom || !statisticsTo))}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          left: direction < 0 ? 0 : undefined,
                          right: direction > 0 ? 0 : undefined,
                          zIndex: 2,
                          width: 24,
                          height: 24,
                          border: `1px solid ${T.borderStrong}`,
                          backgroundColor: T.controlSurface,
                          color: T.textPrimary
                        }}>
                        <AddIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ))}
                    {!datasets.length ? (
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted, fontWeight: 600, textAlign: 'center', px: 2 }}>
                        No statistics datasets are configured.
                      </Box>
                    ) : chartLoading ? (
                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: T.textMuted, fontWeight: 600 }}>
                        <CircularProgress size={32} thickness={4.5} sx={{ color: T.textMuted }} />
                        <span>Loading statistics...</span>
                      </Box>
                    ) : chartError ? (
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b91c1c', fontWeight: 600 }}>{chartError}</Box>
                    ) : datasetData.length === 0 ? (
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted }}>No statistics available for this dataset.</Box>
                    ) : isSignalOverlapSelected ? (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'grid',
                          gridTemplateRows: 'repeat(4, minmax(66px, 1fr)) minmax(120px, 1.35fr) minmax(82px, 1fr)',
                          gap: 1
                        }}>
                        {SIGNAL_OVERLAP_FIELDS.map((field) => (
                          <SignalBooleanChart
                            key={field.key}
                            data={datasetData}
                            fields={[field]}
                            title={field.label}
                            selectionRange={selectionRange}
                            accentColor={accentColor}
                            onSelectionStart={handleChartSelectionStart}
                            onSelectionMove={handleChartSelectionMove}
                            onSelectionEnd={handleChartSelectionEnd}
                            onSelectionReset={resetSelection}
                          />
                        ))}
                        <SignalBooleanChart
                          data={datasetData}
                          fields={SIGNAL_OVERLAP_FIELDS}
                          title="Overlap"
                          selectionRange={selectionRange}
                          accentColor={accentColor}
                          onSelectionStart={handleChartSelectionStart}
                          onSelectionMove={handleChartSelectionMove}
                          onSelectionEnd={handleChartSelectionEnd}
                          onSelectionReset={resetSelection}
                          showLegend
                          offsetLines
                        />
                        <SignalBooleanChart
                          data={datasetData}
                          fields={[SIGNAL_MAIN_FIELD]}
                          title={SIGNAL_MAIN_FIELD.label}
                          selectionRange={selectionRange}
                          accentColor={accentColor}
                          onSelectionStart={handleChartSelectionStart}
                          onSelectionMove={handleChartSelectionMove}
                          onSelectionEnd={handleChartSelectionEnd}
                          onSelectionReset={resetSelection}
                          showXAxis
                        />
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={chartData}
                          style={{ cursor: excludeTimes ? EXCLUDE_TIMES_CURSOR : 'inherit' }}
                          margin={{ top: 10, right: 8, left: 0, bottom: 4 }}
                          onMouseDown={excludeTimes || snappingStopRange ? undefined : handleChartSelectionStart}
                          onMouseMove={excludeTimes || snappingStopRange ? undefined : handleChartSelectionMove}
                          onMouseUp={excludeTimes || snappingStopRange ? undefined : handleChartSelectionEnd}
                          onClick={excludeTimes ? handleGapClick : snappingStopRange ? handleStopGapClick : undefined}
                          onMouseLeave={resetSelection}>
                          <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine} vertical={false} />
                          <defs>
                            <pattern id="statistics-excluded-gap-pattern" patternUnits="userSpaceOnUse" width="12" height="12">
                              <rect width="12" height="12" fill="#374151" fillOpacity="0.18" />
                              <path d="M0 0L12 12M12 0L0 12" stroke="#dc2626" strokeWidth="1" strokeOpacity="0.5" />
                            </pattern>
                          </defs>
                          <XAxis
                            dataKey="date"
                            tickFormatter={(value) => formatAxisTick(value, productionStops.timeZone)}
                            tick={{ fill: T.textMuted, fontSize: 10 }}
                            tickLine={false}
                            axisLine={{ stroke: T.axisLine }}
                            minTickGap={18}
                          />
                          {isOverviewSelected ? (
                            <YAxis
                              yAxisId="efficiency"
                              domain={[0, (dataMax) => Math.max(100, Math.ceil((dataMax || 0) / 10) * 10)]}
                              tickFormatter={(value) => `${value}%`}
                              tick={{ fill: `${T.efficiency}cc`, fontSize: 10 }}
                              tickLine={false}
                              axisLine={false}
                              width={42}
                            />
                          ) : (
                            <>
                              <YAxis
                                yAxisId="speed"
                                tickFormatter={speedUnit ? (value) => `${value} ${speedUnit}` : undefined}
                                tick={{ fill: `${T.expectedSpeed}cc`, fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                width={speedUnit ? 72 : 42}
                              />
                              <YAxis
                                yAxisId="efficiency"
                                orientation="right"
                                domain={[0, (dataMax) => Math.max(100, Math.ceil((dataMax || 0) / 10) * 10)]}
                                tickFormatter={(value) => `${value}%`}
                                tick={{ fill: `${T.efficiency}cc`, fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                width={42}
                              />
                            </>
                          )}
                          {!!visibleStatisticsOverlaySeries.length && <YAxis yAxisId="statistics-overlay" hide domain={[0, 'auto']} />}
                          <Tooltip
                            content={
                              <StatisticsTooltip
                                dataset={selectedDataset}
                                dailyProduction={dailyProduction}
                                stops={productionStops.stops}
                                timeZone={productionStops.timeZone}
                                entryDataLoading={entryData.loading}
                                gapExclusions={unassignedStopMinutes != null ? gapExclusions : undefined}
                              />
                            }
                          />
                          <Legend wrapperStyle={{ fontSize: 11, color: T.textPrimary }} />
                          {entryPlotRanges.map((range) => (
                            <ReferenceArea
                              key={`entry-filter-${range.filterId}-${range.start}`}
                              yAxisId="speed"
                              shape={({ x, y, width, height }) => (
                                <rect
                                  x={x + width * range.left}
                                  y={y}
                                  width={width * (range.right - range.left)}
                                  height={height}
                                  fill={range.color}
                                  fillOpacity={0.16}
                                  stroke={range.color}
                                  strokeOpacity={0.7}
                                  pointerEvents="none"
                                />
                              )}
                            />
                          ))}
                          {bestHourPlotRanges.map((range) => (
                            <ReferenceArea
                              key={range.key}
                              yAxisId="speed"
                              shape={({ x, y, width, height }) => (
                                <rect
                                  x={x + width * range.left}
                                  y={y}
                                  width={width * (range.right - range.left)}
                                  height={height}
                                  fill={accentColor}
                                  fillOpacity={0.12}
                                  stroke={accentColor}
                                  strokeOpacity={0.3}
                                />
                              )}
                            />
                          ))}
                          {unassignedStopMinutes != null &&
                            gapExclusions.unassignedStopGaps.map((gap) => {
                              const left = getProductionTimePosition(chartData, gap.start);
                              const right = getProductionTimePosition(chartData, gap.end);
                              return (
                                <ReferenceArea
                                  key={`default-ledinek-${gap.start}`}
                                  yAxisId="speed"
                                  shape={({ x, y, width, height }) => (
                                    <rect
                                      x={x + width * left}
                                      y={y}
                                      width={width * (right - left)}
                                      height={height}
                                      fill="#f7c315"
                                      fillOpacity={0.04}
                                      stroke="#f7c315"
                                      strokeOpacity={0.5}
                                      strokeDasharray="4 3"
                                      pointerEvents="none"
                                    />
                                  )}
                                />
                              );
                            })}
                          {productionStops.stops.map((stop) => {
                            const zeroMinutes = getExcludedMinutes(stop.start, stop.end, gapExclusions.gaps);
                            const excludedMinutes = getExcludedMinutes(stop.start, stop.end, gapExclusions.excludedGaps);
                            const excluded = zeroMinutes > 0 && Math.abs(zeroMinutes - excludedMinutes) < 0.000001;
                            const left = getProductionTimePosition(chartData, stop.start);
                            const right = getProductionTimePosition(chartData, stop.end);
                            const canToggle = excludeTimes && zeroMinutes > 0;
                            const label = `${stop.issue}: ${getProductionStopReason(stop, locale)}${stop.location ? ` · ${stop.location}` : ''}: ${formatTooltipDate(
                              stop.started_at,
                              productionStops.timeZone
                            )} – ${formatTooltipDate(stop.ended_at, productionStops.timeZone)}; ${formatStatValue(excludedMinutes, 2)} of ${formatStatValue(
                              zeroMinutes,
                              2
                            )} zero-output minutes excluded`;
                            const toggle = (event) => {
                              event.stopPropagation();
                              if (canToggle) gapExclusions.toggleStop(stop);
                            };
                            return (
                              <ReferenceArea
                                key={`stop-${stop.id}`}
                                yAxisId="speed"
                                shape={({ x, y, width, height }) => (
                                  <g
                                    role="button"
                                    aria-label={label}
                                    aria-pressed={excluded ? true : excludedMinutes > 0 ? 'mixed' : false}
                                    aria-disabled={!canToggle}
                                    tabIndex={canToggle ? 0 : -1}
                                    style={{
                                      pointerEvents: excludeTimes ? 'all' : 'none',
                                      cursor: excludeTimes ? (zeroMinutes > 0 ? (excluded ? RESTORE_TIMES_CURSOR : EXCLUDE_TIMES_CURSOR) : 'not-allowed') : 'inherit'
                                    }}
                                    onClick={excludeTimes ? toggle : undefined}
                                    onKeyDown={(event) => {
                                      if (canToggle && (event.key === 'Enter' || event.key === ' ')) {
                                        event.preventDefault();
                                        toggle(event);
                                      }
                                    }}>
                                    <title>{label}</title>
                                    <rect
                                      x={x + width * left}
                                      y={y}
                                      width={width * (right - left)}
                                      height={height}
                                      fill={PRODUCTION_STOP_COLOR}
                                      fillOpacity={0.12}
                                      stroke={PRODUCTION_STOP_COLOR}
                                      strokeOpacity={0.4}
                                      style={{ animation: hoveredStopId === stop.id ? 'statisticsStopPulse 1.2s ease-out infinite' : undefined }}
                                    />
                                    {gapExclusions.excludedGaps
                                      .filter((gap) => gap.start < stop.end && gap.end > stop.start)
                                      .map((gap) => {
                                        const partLeft = getProductionTimePosition(chartData, Math.max(stop.start, gap.start));
                                        const partRight = getProductionTimePosition(chartData, Math.min(stop.end, gap.end));
                                        return (
                                          <rect key={gap.key} x={x + width * partLeft} y={y} width={width * (partRight - partLeft)} height={height} fill={PRODUCTION_STOP_COLOR} fillOpacity={0.35} />
                                        );
                                      })}
                                    <text
                                      x={x + (width * (left + right)) / 2}
                                      y={y + 28}
                                      textAnchor="middle"
                                      fill={stop.issue === 'ledinek' ? '#f7c315' : '#5a7411'}
                                      fontSize={24}
                                      fontWeight={800}
                                      pointerEvents="none">
                                      {stop.issue?.[0]?.toUpperCase()}
                                    </text>
                                  </g>
                                )}
                              />
                            );
                          })}
                          {gapExclusions.gaps
                            .filter((gap) => !gap.hasStop && (excludeTimes || gapExclusions.excludedGaps.includes(gap)))
                            .map((gap) => {
                              const excluded = gapExclusions.excludedGaps.includes(gap);
                              const left = getProductionTimePosition(chartData, gap.start);
                              const right = getProductionTimePosition(chartData, gap.end);
                              const label = `${excluded ? 'Restore' : 'Exclude'} zero-output gap: ${formatTooltipDate(gap.start, productionStops.timeZone)} - ${formatTooltipDate(
                                gap.end,
                                productionStops.timeZone
                              )}`;
                              return (
                                <ReferenceArea
                                  key={`gap-${gap.key}`}
                                  yAxisId="speed"
                                  shape={({ x, y, width, height }) => (
                                    <rect
                                      x={x + width * left}
                                      y={y}
                                      width={Math.max(0, width * (right - left))}
                                      height={height}
                                      fill={excluded ? 'url(#statistics-excluded-gap-pattern)' : '#374151'}
                                      fillOpacity={excluded ? 1 : 0.04}
                                      stroke="#dc2626"
                                      strokeOpacity={excluded ? 0.5 : 0}
                                      style={{ pointerEvents: excludeTimes ? 'all' : 'none', cursor: excludeTimes ? (excluded ? RESTORE_TIMES_CURSOR : EXCLUDE_TIMES_CURSOR) : 'inherit' }}
                                      role="button"
                                      tabIndex={excludeTimes ? 0 : -1}
                                      aria-label={label}
                                      aria-pressed={excluded}
                                      onClick={
                                        excludeTimes
                                          ? (event) => {
                                              event.stopPropagation();
                                              gapExclusions.toggleGap(gap.key);
                                            }
                                          : undefined
                                      }
                                      onKeyDown={(event) => {
                                        if (excludeTimes && (event.key === 'Enter' || event.key === ' ')) {
                                          event.preventDefault();
                                          gapExclusions.toggleGap(gap.key);
                                        }
                                      }}>
                                      <title>{label}</title>
                                    </rect>
                                  )}
                                />
                              );
                            })}
                          {isErrorHighlightActive &&
                            errorHighlightRanges.map((range) => (
                              <ReferenceArea
                                key={`error-${range.x1}-${range.x2}`}
                                yAxisId={chartSelectionYAxisId}
                                ifOverflow="hidden"
                                x1={range.x1}
                                x2={range.x2}
                                strokeOpacity={0}
                                fill="none"
                                shape={({ x, y, width, height, clipPath }) => {
                                  const barH = Math.round((height / 2) * (range.ratio ?? 1));
                                  return <rect clipPath={clipPath} x={x} y={y} width={Math.max(0, width)} height={barH} fill="#dc2626" fillOpacity={range.fillOpacity ?? 0.08} />;
                                }}
                              />
                            ))}
                          {isNoFlowHighlightActive &&
                            noFlowHighlightRanges.map((range) => (
                              <ReferenceArea
                                key={`no-flow-${range.x1}-${range.x2}`}
                                yAxisId={chartSelectionYAxisId}
                                ifOverflow="hidden"
                                x1={range.x1}
                                x2={range.x2}
                                strokeOpacity={0}
                                fill="none"
                                shape={({ x, y, width, height, clipPath }) => {
                                  const barH = Math.round((height / 2) * (range.ratio ?? 1));
                                  return <rect clipPath={clipPath} x={x} y={y + height - barH} width={Math.max(0, width)} height={barH} fill={T.actualSpeed} fillOpacity={range.fillOpacity ?? 0.08} />;
                                }}
                              />
                            ))}
                          {selectionRange && (
                            <ReferenceArea
                              yAxisId={chartSelectionYAxisId}
                              ifOverflow="hidden"
                              x1={selectionRange[0]}
                              x2={selectionRange[1]}
                              stroke={selectingStopRange ? PRODUCTION_STOP_COLOR : accentColor}
                              strokeOpacity={0.35}
                              fill={selectingStopRange ? PRODUCTION_STOP_COLOR : accentColor}
                              fillOpacity={0.18}
                              isFront
                            />
                          )}
                          {stopSelectionGaps.map((gap) => {
                            const left = getProductionTimePosition(chartData, gap.start);
                            const right = getProductionTimePosition(chartData, gap.end);
                            const label = `${formatTooltipDate(gap.start, productionStops.timeZone)} - ${formatTooltipDate(gap.end, productionStops.timeZone)}`;
                            return (
                              <ReferenceArea
                                key={`stop-selection-${gap.key}`}
                                yAxisId="speed"
                                shape={({ x, y, width, height }) => (
                                  <rect
                                    x={x + width * left}
                                    y={y}
                                    width={Math.max(0, width * (right - left))}
                                    height={height}
                                    fill={PRODUCTION_STOP_COLOR}
                                    fillOpacity={0.08}
                                    stroke={PRODUCTION_STOP_COLOR}
                                    strokeOpacity={0}
                                    className="statistics-stop-selection-gap"
                                    style={{ cursor: 'pointer' }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={label}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      selectStopRange(gap.start, gap.end);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        selectStopRange(gap.start, gap.end);
                                      }
                                    }}>
                                    <title>{label}</title>
                                  </rect>
                                )}
                              />
                            );
                          })}
                          {isOverviewSelected ? (
                            <>
                              {overviewEfficiencyDatasets.map((dataset) => (
                                <Line
                                  key={dataset.key}
                                  yAxisId="efficiency"
                                  type="monotone"
                                  dataKey={`efficiency_${dataset.key}`}
                                  name={dataset.label}
                                  stroke={dataset.color}
                                  strokeWidth={1.8}
                                  dot={false}
                                  isAnimationActive={false}
                                  connectNulls
                                />
                              ))}
                              <Line
                                yAxisId="efficiency"
                                type="monotone"
                                dataKey={OVERVIEW_AVERAGE_KEY}
                                name="Average Efficiency"
                                stroke={T.textPrimary}
                                strokeWidth={2.8}
                                dot={false}
                                isAnimationActive={false}
                                connectNulls
                              />
                            </>
                          ) : (
                            <>
                              <Line
                                yAxisId="speed"
                                type="monotone"
                                dataKey="actualSpeed"
                                name={`${selectedDataset?.actualSpeedLabel ?? 'Actual Speed'}${speedUnitSuffix}`}
                                stroke={T.actualSpeed}
                                strokeWidth={2.4}
                                dot={false}
                                isAnimationActive={false}
                              />
                              <Line
                                yAxisId="speed"
                                type="monotone"
                                dataKey="expectedSpeed"
                                name={`${selectedDataset?.expectedSpeedLabel ?? 'Expected Speed'}${speedUnitSuffix}`}
                                stroke={T.expectedSpeed}
                                strokeWidth={2.1}
                                strokeDasharray="5 3"
                                dot={false}
                                isAnimationActive={false}
                              />
                              <Line yAxisId="efficiency" type="monotone" dataKey="efficiency" name="Efficiency" stroke={T.efficiency} strokeWidth={2.2} dot={false} isAnimationActive={false} />
                            </>
                          )}
                          {visibleStatisticsOverlaySeries.map((series) => (
                            <Line
                              key={series.identifier}
                              yAxisId="statistics-overlay"
                              type="monotone"
                              dataKey={series.dataKey}
                              name={`${series.label} (Σ)`}
                              stroke={series.color}
                              unit={statisticsOverlayGraph.unit}
                              strokeWidth={1.5}
                              strokeDasharray="3 3"
                              dot={false}
                              isAnimationActive={false}
                              connectNulls
                            />
                          ))}
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                )}
                {entrySpectrogramField && statisticsSections?.speedChart !== false && (
                  <StatisticsSpectrogram
                    title={entrySpectrogramField.label}
                    data={entrySpectrogramData}
                    loading={chartLoading || entryData.loading}
                    leftInset={(speedUnit ? 72 : 42) + 24}
                    rightInset={74}
                  />
                )}
                {selectedDataset?.showDimensionSpectrogram && statisticsSections?.speedChart !== false && (
                  <StatisticsSpectrogram title="Dimension switches" data={dimensionSpectrogramData} loading={chartLoading} leftInset={(speedUnit ? 72 : 42) + 24} rightInset={74} />
                )}
              </Box>
            </Portal>

            {selectedDataset?.allowStopAnnotations && (
              <>
                <ProductionStopLog
                  key={selectedDataset.key}
                  productionStops={productionStops}
                  canEdit={canEditStop}
                  canDelete={canDeleteStop}
                  loading={stopClassificationLoading}
                  unassignedStopMinutes={unassignedStopMinutes ?? 0}
                  onEdit={openStop}
                  onLeaveExclusions={leaveExclusions}
                  onHover={setHoveredStopId}
                />
                <ProductionStopDialog
                  open={canManageCurrentStop && editingStop !== undefined && !selectingStopRange}
                  stop={editingStop}
                  dataset={selectedDataset}
                  productionStops={productionStops}
                  onClose={() => setEditingStop(undefined)}
                  onSelectRange={beginStopSelection}
                  canSelectRange={statisticsSections?.speedChart !== false && !chartLoading && !chartError && datasetData.length > 1}
                />
              </>
            )}

            {!isSignalOverlapSelected && (
              <>
                {statisticsSections?.summaryMetrics !== false && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                    {isOverviewSelected ? (
                      <>
                        <MetricCard
                          label={`Average Efficiency${liveMetricLabelSuffix}`}
                          value={formatPercentValue(selectedSummary?.fullRangeEfficiency)}
                          accent={efficiencyMetricColor}
                          calculation={overviewCalculation('efficiency')}
                          loading={loading}
                        />
                        {showContractEfficiency && (
                          <MetricCard
                            label={`Contract Efficiency${liveMetricLabelSuffix}`}
                            value={formatPercentValue(selectedSummary?.fullRangeContractEfficiency)}
                            accent={contractEfficiencyMetricColor}
                            calculation={overviewCalculation('contractEfficiency')}
                            loading={loading}
                          />
                        )}
                        <MetricCard label="Timeframe" value={selectedRangeDuration} accent={T.textPrimary} calculation={cardCalculations.timeframe} />
                      </>
                    ) : (
                      <>
                        {selectedDataset?.productionCards?.length > 0 && (
                          <Box sx={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                            {selectedDataset.productionCards
                              .filter((key) => productionCards[key])
                              .map((key) => (
                                <MetricCard
                                  key={key}
                                  {...productionCards[key]}
                                  calculation={dailyProduction.loading ? <FormattedMessage {...globalMessages.statisticsProductionDataLoading} /> : cardCalculations[key]}
                                  loading={dailyProduction.loading}
                                  accent={accentColor}
                                />
                              ))}
                          </Box>
                        )}
                        {metricCardOrder
                          .filter((key) => metricCards[key])
                          .map((key) => (
                            <MetricCard key={key} {...metricCards[key]} calculation={cardCalculations[key]} loading={loading && key !== 'timeframe'} />
                          ))}
                      </>
                    )}
                  </Box>
                )}

                <ErrorSpectrogram dataByArea={dataByArea} areaIdentifiers={errorAreaIdentifiers} selectedErrorKeys={selectedErrorKeys} excludedErrorKeys={excludedErrorKeys} />

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', alignItems: 'start', gap: 1 }}>
                  <Box sx={{ backgroundColor: T.surfaceAlt, backdropFilter: 'blur(4px)', border: `1px solid ${T.border}`, borderRadius: 1.5, p: 1.25 }}>
                    <Typography sx={{ color: T.textHint, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.75 }}>
                      {isOverviewSelected ? 'Combined Error Minutes' : 'Error Minutes'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.75, flexWrap: 'wrap' }}>
                      <Typography sx={{ color: '#b91c1c', fontSize: 14, fontWeight: 800 }}>Error: {formatErrorDuration(selectedErrorDurationMinutes ?? 0)}</Typography>
                      <Typography sx={{ color: getErrorDurationColor(selectedNoFlowDurationMinutes, selectedErrorDurationMinutes), fontSize: 14, fontWeight: 800 }}>
                        No Flow: {formatErrorDuration(selectedNoFlowDurationMinutes)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gap: 0.5 }}>
                      {selectedErrorGroups.length ? (
                        selectedErrorGroups.map((group) => (
                          <ErrorGroup
                            key={group.key}
                            group={group}
                            totalMinutes={selectedErrorDurationMinutes}
                            totalNoFlowMinutes={selectedNoFlowDurationMinutes}
                            collapsedErrorGroups={collapsedErrorGroups}
                            setCollapsedErrorGroups={setCollapsedErrorGroups}
                            selectedErrorKeys={selectedErrorKeys}
                            setSelectedErrorKeys={setSelectedErrorKeys}
                            excludedErrorKeys={excludedErrorKeys}
                            setExcludedErrorKeys={setExcludedErrorKeys}
                            onErrorSelected={() => {
                              setShowErrorHighlights(true);
                              setShowNoFlowHighlights(true);
                            }}
                          />
                        ))
                      ) : (
                        <Typography sx={{ color: T.textMuted, fontSize: 12 }}>Unavailable</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ backgroundColor: T.surfaceAlt, backdropFilter: 'blur(4px)', border: `1px solid ${T.border}`, borderRadius: 1.5, p: 1.25 }}>
                    <Typography sx={{ color: T.textHint, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.75 }}>
                      {isOverviewSelected ? 'Combined Doubles Avg' : 'Doubles Avg'}
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 0.5 }}>
                      {selectedDoubleEntries.length ? (
                        selectedDoubleEntries.map((entry) => (
                          <Box key={entry.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, color: T.textMuted, fontSize: 12 }}>
                            <span>{entry.name}</span>
                            <strong>{formatStatValue(entry.value)}</strong>
                          </Box>
                        ))
                      ) : (
                        <Typography sx={{ color: T.textMuted, fontSize: 12 }}>Unavailable</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </StyledSidebarPanel>
      </StyledSidebarShell>
    </Box>
  );
}
